import { put, takeLatest, select } from 'redux-saga/effects'
import { getType } from 'typesafe-actions'

import {
    approveCalendar,
    saveCalendar,
    deleteCalendar,
    createCalendar,
    getCalendarTypes,
    getCalendarList,
    deleteSupplierCalendar
} from 'redux/actions'
import { get, del, patch, post } from 'shared/api/api'
import { calendarsSelector } from 'redux/selectors'
import { CalendarTypes } from 'shared/api/calendarsApi'
import {
    RequestLocationCalendars,
    LocationDates,
    ICalendar,
    ICreateCalendarResponse,
    ICreateUnavailableDaysDto
} from 'pages/my-calendars/add-calendar'
import { ERROR_MODIFY_APPROVED_CALENDAR } from 'shared/api/errors'
import { CalendarState } from 'redux/reducers'
import { IQuotaError } from 'shared/interfaces/redux'
import { getUpdatedDates } from 'shared/utils/calendar.utils'

const postNewCalendar = async (supplierId: string) =>
    await post({
        url: `/api/suppliers/${supplierId}/calendars`,
        data: { name: '123', calendarTypeId: '2fa94639-277d-4782-ba35-8ee8a60689ba' }
    })

function* getCalendarListSaga() {
    try {
        const userSupplierId: string = yield localStorage.getItem('supplierId')
        const list: ICalendar[] = yield get({ url: `/api/suppliers/${userSupplierId}/calendars` })

        yield put(getCalendarList.success(list))
    } catch (e) {
        console.log(e)
    }
}

function* getTypesSaga() {
    try {
        const types: CalendarTypes[] = yield get({ url: '/api/calendar-types' })

        yield put(getCalendarTypes.success(types.filter((el) => el.activeInd)))
    } catch (e) {
        console.log(e)
    }
}

function* createCalendarSaga(action: ReturnType<typeof createCalendar.request>) {
    try {
        const userSupplierId: string = yield localStorage.getItem('supplierId')
        const { id: calendarId }: ICreateCalendarResponse = yield post({
            url: `/api/suppliers/${userSupplierId}/calendars`,
            data: action.payload
        })

        yield put(createCalendar.success({ calendarId }))
    } catch (e) {
        console.log(e)
    }
}

function* saveCalendarSaga(action: ReturnType<typeof saveCalendar.request>) {
    try {
        const calendarStore: CalendarState = yield select(calendarsSelector)
        const userSupplierId: string = yield localStorage.getItem('supplierId')
        const { calendarItems, isAutoSave, isEdit, prevDates, isApprove } = action.payload
        const calendarData = { id: calendarStore.calendarId || '' }

        if (isEdit) {
            const dataToDelete: string[] = []
            const currentAdditions = calendarItems.filter((el) => el.id).map((el) => el.id)
            const previousAdditions = prevDates.filter((el) => el.id)

            previousAdditions.forEach((el) => {
                if (!currentAdditions.includes(el.id)) {
                    dataToDelete.push(el.id)
                }
            })

            const additions: ICreateUnavailableDaysDto[] = calendarItems.reduce(
                (acc: ICreateUnavailableDaysDto[], calendar) =>
                    !calendar.id
                        ? [
                              ...acc,
                              {
                                  locationId: calendar.locationId,
                                  lastOrderDate: calendar.lastOrderDate || null,
                                  firstOrderDate: calendar.firstOrderDate || null,
                                  orderDate: calendar.orderDate || null,
                                  deliveryDate: calendar.deliveryDate || null
                              }
                          ]
                        : acc,
                []
            )

            const dateset = calendarItems.reduce(
                (prev: LocationDates[], curr: RequestLocationCalendars) =>
                    curr.id
                        ? [
                              ...prev,
                              {
                                  id: curr.id,
                                  lastOrderDate: curr.lastOrderDate || null,
                                  firstOrderDate: curr.firstOrderDate || null,
                                  orderDate: curr.orderDate || null,
                                  deliveryDate: curr.deliveryDate || null
                              }
                          ]
                        : prev,
                []
            )

            const updatedDates = getUpdatedDates(prevDates, dateset)

            if (dataToDelete.length) {
                yield del({
                    url: `/api/suppliers/${userSupplierId}/calendars/${calendarData.id}/unavailable-days`,
                    data: { dateIds: dataToDelete }
                })
            }

            if (additions.length) {
                yield post({
                    url: `/api/suppliers/${userSupplierId}/calendars/${calendarData.id}/unavailable-days`,
                    data: { dateset: additions }
                })
            }

            if (updatedDates.length) {
                yield patch({
                    url: `/api/suppliers/${userSupplierId}/calendars/${calendarData.id}/unavailable-days`,
                    data: { dateset: updatedDates }
                })
            }
        } else {
            const dateset = calendarItems.map((el) => ({
                locationId: el.locationId,
                lastOrderDate: el.lastOrderDate || null,
                firstOrderDate: el.firstOrderDate || null,
                orderDate: el.orderDate || null,
                deliveryDate: el.deliveryDate || null
            }))

            yield post({
                url: `/api/suppliers/${userSupplierId}/calendars/${calendarData.id}/unavailable-days`,
                data: { dateset }
            })
        }

        yield put(saveCalendar.success({ calendarId: calendarData.id, isAutoSave }))

        if (isApprove) {
            yield put(approveCalendar.request())
        }
    } catch (error) {
        if (
            (error as IQuotaError).errors.findIndex(
                (it) => it.code === ERROR_MODIFY_APPROVED_CALENDAR
            ) !== -1
        ) {
            yield put(saveCalendar.failure())
        }
    }
}

function* approveCalendarSaga() {
    try {
        const calendarStore: CalendarState = yield select(calendarsSelector)
        const userSupplierId: string = yield localStorage.getItem('supplierId')
        let calendarData = { id: calendarStore.calendarId || '' }

        if (!calendarData.id) {
            calendarData = yield postNewCalendar(userSupplierId)
        }

        yield patch({
            url: `/api/suppliers/${userSupplierId}/calendars/${calendarData.id}`
        })

        yield put(approveCalendar.success({ calendarId: calendarData.id }))
    } catch (e) {
        console.log(e)
    }
}

// удаление календаря
function* deleteCalendarSaga(action: ReturnType<typeof deleteCalendar.request>) {
    try {
        const calendarStore: CalendarState = yield select(calendarsSelector)
        const userSupplierId: string = yield localStorage.getItem('supplierId')

        if (action.payload || calendarStore.calendarId) {
            yield del({
                url: `/api/suppliers/${userSupplierId}/calendars/${
                    action.payload || calendarStore.calendarId
                }`
            })
        }

        if (action.payload) {
            const list: ICalendar[] = yield get({
                url: `/api/suppliers/${userSupplierId}/calendars`
            })

            yield put(getCalendarList.success(list))
        }

        yield put(deleteCalendar.success())
    } catch (e) {
        console.log(e)
    }
}

// удаление утвержденного календаря
function* deleteSupplierCalendarSaga(action: ReturnType<typeof deleteSupplierCalendar.request>) {
    try {
        const calendarStore: CalendarState = yield select(calendarsSelector)
        const userSupplierId: string = yield localStorage.getItem('supplierId')

        if (action.payload || calendarStore.calendarId) {
            yield del({
                url: `/api/partners/Shipping-Calendar/deleteSupplierCalendar/${
                    action.payload || calendarStore.calendarId
                }`
            })
        }

        if (action.payload) {
            const list: ICalendar[] = yield get({
                url: `/api/suppliers/${userSupplierId}/calendars`
            })

            yield put(getCalendarList.success(list))
        }

        yield put(deleteSupplierCalendar.success())
    } catch (e) {
        console.log(e)
    }
}

export const calendarSaga = [
    takeLatest(getType(saveCalendar.request), saveCalendarSaga),
    takeLatest(getType(approveCalendar.request), approveCalendarSaga),
    takeLatest(getType(deleteCalendar.request), deleteCalendarSaga),
    takeLatest(getType(deleteSupplierCalendar.request), deleteSupplierCalendarSaga),
    takeLatest(getType(createCalendar.request), createCalendarSaga),
    takeLatest(getType(getCalendarTypes.request), getTypesSaga),
    takeLatest(getType(getCalendarList.request), getCalendarListSaga)
]
